import { assign, uid } from '../utils/Common'
import Point, { pt } from '../utils/Point'
import { Direction } from '../utils/SwipeHelper'
import Exit from './cards/Exit'
import Gold from './cards/Gold'
import Monster, { MonsterAbility } from './cards/Monster'
import Player from './cards/Player'
import Potion from './cards/Potion'
import Spell from './cards/Spell'
import Deck from './Deck'
import GameBoard from './GameBoard'

export const enum CardType {
  None = 'none',
  Hero = 'hero',
  Monster = 'monster',
  Exit = 'exit',
  Potion = 'potion',
  Gold = 'gold',
  Spell = 'spell'
}

export const enum CardStatus {
  Alerted,
  Poisoned,
}

export default class Card {
  id = uid()
  name = ''
  deck?: Deck

  statuses: CardStatus[] = []

  constructor(public board?: GameBoard, public location?: Point) {

  }

  adjacentCards() {
    return [pt(0, 1), pt(0, -1), pt(1, 0), pt(-1, 0)]
      .map(pt => this.board!.cardAt(this.location!.x + pt.x, this.location!.y + pt.y))
      .filter(card => !!card) as Card[]
  }

  canPick(picker: Monster = this.board!.hero.card): boolean {
    return !!this.adjacentCards().find(card =>
      card === picker &&
      Math.abs(this.getHeight() - card.getHeight()) <= 1
    )
  }

  getHeight() {
    return this.board!.deckAt(this.location!)!.size
  }

  pick(picker: Monster = this.board!.hero.card) {
    return this.canPick(picker)
  }

  moveTo(deckOrLocation?: Point | Deck, board?: GameBoard, skipEvents = false) {
    if (board) {
      this.board = board || (deckOrLocation instanceof Deck ? deckOrLocation.board : board)
    }

    this.deck?.remove(this)
    if (this.location)
      this.board?.cards.delete(this.location)

    if (deckOrLocation instanceof Deck) {
      const deck = deckOrLocation
      delete this.location
      deck?.add(this)
    } else if (deckOrLocation instanceof Point) {
      const location = deckOrLocation
      if (this.location)
        this.board!.cards.delete(this.location)
      this.board!.cards.set(location, this)
      this.location = location
    }

    return this
  }

  riseStack(scanned = new Map<Card, number>()) {
    const currentDeck = this.board!.deckAt(this.location!.x, this.location!.y)!
    this.adjacentCards()
      .filter(card => {
        const isExit = card instanceof Exit
        return isExit ? card.getHeight() < this.getHeight() : true
      })
      .map(card => {
        const deck = this.board!.deckAt(card.location!)!
        let diff = Math.abs(currentDeck.size - deck.size)
        while (diff --> 1) deck.add(card.clone())

        return card
      })
      .map(card => {
        if (scanned.get(card)) return
        scanned.set(card, 1)

        card.riseStack(scanned)
      })
  }

  drawCards() {
    this.board!.drawCards()
  }

  hasStatus(status: CardStatus) {
    return this.statuses.includes(status)
  }

  clone(): Card {
    return assign(new Card(), {
      name: this.name
    })
  }
}
